import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SentIcon,
  Cancel01Icon,
  PencilEdit02Icon,
  RefreshIcon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { IconAI } from "shared/components/ai/IconAI";
import { formatPhone } from "shared/utils";
import { MessageCardLayout } from "./MessageCard";
import { TranslateComposerButton } from "./TranslateComposerButton";
import { useInPlaceTranslation } from "../hooks/useInPlaceTranslation";
import { useInboxEmbed } from "../context/useInboxEmbed";
import type { SuggestedReply } from "../types";

interface SuggestedReplyCardProps {
  suggestion: SuggestedReply;
  onSend: (text: string) => void;
  onDismiss: () => void;
  onRetry?: () => void;
  isRetrying?: boolean;
  isSending?: boolean;
  sendDisabled?: boolean;
  sendDisabledReason?: string;
  customerPhone?: string;
}

const EditToggleButton = ({
  isCompact,
  tooltipZIndex,
  isEditing,
  disabled,
  onToggle
}: {
  isCompact: boolean;
  tooltipZIndex?: number;
  isEditing: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) => {
  const icon = isEditing ? Cancel01Icon : PencilEdit02Icon;
  const label = isEditing ? "Cancel" : "Edit";

  if (isCompact)
    return (
      <IconButton
        HugeIcon={icon}
        variant={IconButtonVariant.BASE}
        size={IconButtonSize.SM}
        onClick={onToggle}
        disabled={disabled}
        tooltip={label}
        tooltipZIndex={tooltipZIndex}
        aria-label={label}
      />
    );

  return (
    <Button
      HugeIcon={icon}
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      onClick={onToggle}
      disabled={disabled}
    >
      {label}
    </Button>
  );
};

const DismissButton = ({
  isCompact,
  tooltipZIndex,
  disabled,
  onClick
}: {
  isCompact: boolean;
  tooltipZIndex?: number;
  disabled?: boolean;
  onClick: () => void;
}) => {
  if (isCompact)
    return (
      <IconButton
        HugeIcon={Cancel01Icon}
        variant={IconButtonVariant.BASE}
        size={IconButtonSize.SM}
        strokeWidth={3}
        onClick={onClick}
        disabled={disabled}
        className="shrink-0"
        tooltip="Dismiss"
        tooltipZIndex={tooltipZIndex}
        aria-label="Dismiss"
      />
    );

  return (
    <Button
      HugeIcon={Cancel01Icon}
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      iconStrokeWidth={3}
      onClick={onClick}
      disabled={disabled}
      className="shrink-0"
    >
      Dismiss
    </Button>
  );
};

const SuggestedReplyHeader = ({
  customerPhone
}: {
  customerPhone?: string;
}) => (
  <div className="flex items-center gap-2 text-sm font-medium text-content-200">
    <span>Leasing agent</span>
    <HugeiconsIcon
      icon={InformationCircleIcon}
      size={16}
      className="text-content-200"
    />
    <span>
      Based on: prior thread
      {customerPhone ? ` with ${formatPhone(customerPhone)}` : ""}
    </span>
  </div>
);

// Email suggestions are sent reply-all, so staff see the exact recipient list
// before pressing Send.
const formatReplyRecipients = (suggestion: SuggestedReply): string => {
  const recipients = suggestion.reply_recipients;
  if (suggestion.channel !== "email" || !recipients?.to.length) return "";
  const toPart = `To: ${recipients.to.join(", ")}`;
  return recipients.cc.length > 0
    ? `${toPart} · Cc: ${recipients.cc.join(", ")}`
    : toPart;
};

export const SuggestedReplyCard = ({
  suggestion,
  onSend,
  onDismiss,
  onRetry,
  isRetrying,
  isSending,
  sendDisabled,
  sendDisabledReason,
  customerPhone
}: SuggestedReplyCardProps) => {
  const { status, text, failed_reason } = suggestion;
  // compact: icon-only buttons for the narrow message pop-up.
  // modalZIndex: clears the pop-up's own stacking so the tooltip isn't
  // rendered behind it.
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(text ?? "");
  const recipientsLine = formatReplyRecipients(suggestion);

  const translation = useInPlaceTranslation({
    getCurrentText: () => draft,
    applyText: setDraft
  });

  // Keep the local draft in sync if the suggestion text changes (e.g. a
  // regeneration arrives via WebSocket) while the user hasn't started editing.
  // A fresh suggestion is new content, so drop any stale undo snapshot/toggle.
  useEffect(() => {
    if (!isEditing) {
      setDraft(text ?? "");
      translation.reset();
      translation.syncDraftLanguage(text ?? "");
    }
  }, [text, isEditing, translation.reset, translation.syncDraftLanguage]);

  return (
    <MessageCardLayout
      isFromContact={false}
      avatar={<IconAI bgSize={32} />}
      header={<SuggestedReplyHeader customerPhone={customerPhone} />}
    >
      <div className="ai-animated-border w-full max-w-[580px] rounded-2xl rounded-tr-none overflow-hidden">
        {/* Body */}
        <div className="px-5 py-4">
          {status === "generating" && (
            <div className="flex items-center gap-2 text-content-300">
              <span className="size-4 animate-spin rounded-full border-2 border-alpha-200 border-t-primary-500" />
              <span className="text-base">Generating response…</span>
            </div>
          )}

          {status === "failed" && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-danger-500">
                <HugeiconsIcon icon={AlertCircleIcon} size={20} />
                <span className="text-base font-medium">
                  {failed_reason || "Couldn't generate a suggestion"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {onRetry ? (
                  <Button
                    HugeIcon={RefreshIcon}
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    onClick={onRetry}
                    loading={isRetrying}
                  >
                    Retry
                  </Button>
                ) : null}
                <Button
                  HugeIcon={Cancel01Icon}
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  iconStrokeWidth={3}
                  onClick={onDismiss}
                  disabled={isRetrying}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <Show if={status === "ready" && Boolean(recipientsLine)}>
            <p className="mb-3 text-sm text-content-400">{recipientsLine}</p>
          </Show>

          {status === "ready" &&
            (isEditing ? (
              <textarea
                aria-label="Edit suggested reply"
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  translation.clearUndo();
                  translation.syncDraftLanguage(event.target.value);
                }}
                rows={4}
                autoFocus
                disabled={isSending}
                className="w-full resize-y rounded-lg border border-alpha-200 bg-white p-3 text-base leading-relaxed text-content-1000 outline-none focus:border-primary-500"
              />
            ) : (
              <p className="whitespace-pre-wrap text-base leading-relaxed text-content-1000">
                {draft}
              </p>
            ))}
        </div>

        {/* Actions */}
        {status === "ready" && (
          <div className="flex items-start gap-3 px-5 pb-4">
            {/* Translate wraps to line 2 first when tight; Dismiss stays fixed. */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              <Button
                HugeIcon={SentIcon}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                onClick={() => onSend(draft)}
                loading={isSending}
                disabled={
                  isSending || draft.trim().length === 0 || sendDisabled
                }
                tooltip={sendDisabled ? sendDisabledReason : undefined}
                tooltipZIndex={embed?.modalZIndex}
              >
                Send
              </Button>

              <EditToggleButton
                isCompact={isCompact}
                tooltipZIndex={embed?.modalZIndex}
                isEditing={isEditing}
                disabled={isSending}
                onToggle={() => {
                  if (isEditing) {
                    // Discard edits and restore the AI draft.
                    setDraft(text ?? "");
                    setIsEditing(false);
                  } else setIsEditing(true);
                }}
              />

              <TranslateComposerButton
                canTranslate={translation.canTranslate}
                canUndo={translation.canUndo}
                isTranslating={translation.isTranslating}
                disabled={isSending || draft.trim().length === 0}
                onTranslate={translation.translate}
                onUndo={translation.undo}
              />
            </div>

            <DismissButton
              isCompact={isCompact}
              tooltipZIndex={embed?.modalZIndex}
              disabled={isSending}
              onClick={onDismiss}
            />
          </div>
        )}
      </div>
    </MessageCardLayout>
  );
};
